/**
 * Interaction-capture descriptor building
 *
 * Hybrid autocapture: every click on a semantic target is captured with no
 * template changes required; a `data-track` attribute is the opt-in override
 * for controls whose automatic accessible name is weak (e.g. icon-only
 * buttons). Each interaction is over-logged with a full descriptor —
 * accessible name, role, uniqueness keys and a stable selector path — so it
 * can always be matched back to a specific control. Raw input values are
 * never read (PII); only field identity is captured.
 */

export interface InteractionDescriptor {
  /** Human label for the event — accessible name, falling back to data-track / id / tag. */
  name: string;
  props: Record<string, unknown>;
}

/** Semantic targets, walked up to from the click target. */
const TARGET_SELECTOR = [
  '[data-track]',
  'button',
  'a',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  'input',
  'select',
  'textarea',
  'label'
].join(', ');

const IMPLICIT_ROLES: Record<string, string> = {
  button: 'button',
  a: 'link',
  select: 'combobox',
  textarea: 'textbox',
  label: 'label'
};

const INPUT_TYPE_ROLES: Record<string, string> = {
  checkbox: 'checkbox',
  radio: 'radio',
  button: 'button',
  submit: 'button',
  search: 'searchbox'
};

const MAX_TEXT_LENGTH = 80;
const MAX_PATH_DEPTH = 8;

/**
 * Resolves the semantic element a click should be attributed to, or null for
 * non-semantic clicks (plain divs, page background). A label with an
 * associated control returns null: the browser forwards the click to the
 * control, which is captured instead — one event, better identity.
 */
export function resolveInteractionTarget(eventTarget: EventTarget | null): Element | null {
  if (!(eventTarget instanceof Element)) return null;
  // Opt-out container: tooling UI (e.g. the usage inspector) marks itself
  // data-track-ignore so its own clicks never pollute the metrics.
  if (eventTarget.closest('[data-track-ignore]')) return null;
  const target = eventTarget.closest(TARGET_SELECTOR);
  if (target instanceof HTMLLabelElement && target.control) return null;
  return target;
}

/**
 * All semantic (trackable) elements currently in the document — the same
 * selector the click capture uses, minus opt-out containers. Used by the
 * usage inspector to join recorded metrics back onto the live DOM.
 */
export function collectSemanticTargets(root: ParentNode): Element[] {
  return Array.from(root.querySelectorAll(TARGET_SELECTOR))
    .filter(element => !element.closest('[data-track-ignore]'));
}

export function buildInteractionDescriptor(target: Element): InteractionDescriptor {
  const accessibleName = resolveAccessibleName(target);
  const tag = target.tagName.toLowerCase();
  const dataTrack = target.getAttribute('data-track');
  const componentHost = findComponentHost(target);

  const props: Record<string, unknown> = {
    role: resolveRole(target),
    tag: tag,
    selectorPath: buildSelectorPath(target)
  };
  if (accessibleName.source) props.nameSource = accessibleName.source;
  if (accessibleName.source === 'placeholder') props.weakName = true;
  setIfPresent(props, 'dataTrack', dataTrack);
  setIfPresent(props, 'id', target.id || null);
  setIfPresent(props, 'controlName', target.getAttribute('name'));
  setIfPresent(props, 'formControlName', target.getAttribute('formcontrolname'));
  setIfPresent(props, 'href', target.getAttribute('href'));
  if (target instanceof HTMLInputElement) props.inputType = target.type;
  if (componentHost) {
    props.componentTag = componentHost.tagName.toLowerCase();
    setIfPresent(props, 'componentPressed', componentHost.getAttribute('aria-pressed'));
    setIfPresent(props, 'componentExpanded', componentHost.getAttribute('aria-expanded'));
  }

  return {
    name: accessibleName.name ?? dataTrack ?? (target.id || tag),
    props: props
  };
}

function setIfPresent(props: Record<string, unknown>, key: string, value: string | null): void {
  if (value !== null && value !== '') props[key] = value;
}

/**
 * Accessible-name resolution in the same priority order as our a11y
 * standards: aria-label → aria-labelledby → associated <label> → alt/title →
 * visible text → placeholder (flagged weak so it surfaces unnamed controls).
 */
function resolveAccessibleName(target: Element): { name?: string, source?: string } {
  const ariaLabel = normaliseText(target.getAttribute('aria-label'));
  if (ariaLabel) return { name: ariaLabel, source: 'aria-label' };

  const labelledBy = target.getAttribute('aria-labelledby');
  if (labelledBy) {
    const referenced = labelledBy.split(/\s+/)
      .map(id => normaliseText(document.getElementById(id)?.textContent))
      .filter(text => !!text)
      .join(' ');
    if (referenced) return { name: referenced, source: 'aria-labelledby' };
  }

  const label = findAssociatedLabel(target);
  if (label) return { name: label, source: 'label' };

  const alt = normaliseText(target.getAttribute('alt') ?? target.querySelector('img[alt]')?.getAttribute('alt'));
  if (alt) return { name: alt, source: 'alt' };

  const title = normaliseText(target.getAttribute('title'));
  if (title) return { name: title, source: 'title' };

  const text = normaliseText(target.textContent);
  if (text) return { name: text, source: 'text' };

  const placeholder = normaliseText(target.getAttribute('placeholder'));
  if (placeholder) return { name: placeholder, source: 'placeholder' };

  return {};
}

function findAssociatedLabel(target: Element): string | undefined {
  if (target.id) {
    const forLabel = document.querySelector(`label[for="${cssEscape(target.id)}"]`);
    const forText = normaliseText(forLabel?.textContent);
    if (forText) return forText;
  }
  return normaliseText(target.closest('label')?.textContent);
}

function resolveRole(target: Element): string {
  const explicit = target.getAttribute('role');
  if (explicit) return explicit;
  const tag = target.tagName.toLowerCase();
  if (target instanceof HTMLInputElement) return INPUT_TYPE_ROLES[target.type] ?? 'textbox';
  return IMPLICIT_ROLES[tag] ?? tag;
}

/**
 * Stable selector path: anchored at the nearest ancestor with an id or
 * data-track, then a tag:nth-of-type chain down to the target — unique
 * enough to identify the control without capturing page content.
 */
function buildSelectorPath(target: Element): string {
  const segments: string[] = [];
  let current: Element | null = target;
  while (current && current !== document.body && segments.length < MAX_PATH_DEPTH) {
    if (current !== target) {
      if (current.id) {
        segments.unshift(`#${current.id}`);
        return segments.join(' > ');
      }
      const dataTrack = current.getAttribute('data-track');
      if (dataTrack) {
        segments.unshift(`[data-track="${dataTrack}"]`);
        return segments.join(' > ');
      }
    }
    segments.unshift(`${current.tagName.toLowerCase()}:nth-of-type(${nthOfType(current)})`);
    current = current.parentElement;
  }
  return segments.join(' > ');
}

function nthOfType(element: Element): number {
  let index = 1;
  let sibling = element.previousElementSibling;
  while (sibling) {
    if (sibling.tagName === element.tagName) index++;
    sibling = sibling.previousElementSibling;
  }
  return index;
}

/** Nearest custom-element ancestor (tag containing a hyphen, e.g. app-nav-bar, or-toggle-button). */
function findComponentHost(target: Element): Element | null {
  let current: Element | null = target;
  while (current && current !== document.body) {
    if (current.tagName.includes('-')) return current;
    current = current.parentElement;
  }
  return null;
}

function normaliseText(text: string | null | undefined): string | undefined {
  const normalised = text?.replace(/\s+/g, ' ').trim();
  if (!normalised) return undefined;
  return normalised.length > MAX_TEXT_LENGTH ? normalised.slice(0, MAX_TEXT_LENGTH) : normalised;
}

/** CSS.escape with a conservative fallback for older embedded browsers. */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}
